import { useCallback, useEffect, useState } from "react";
import { LuCopyPlus } from "react-icons/lu";
import { SlGraph } from "react-icons/sl";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroCategoriaMestra from "../../components/modais/ModalCadastroCategoriaMestra";
import { repositorioCategoriaFinanceira, repositorioCategoriaMestra } from "../../sync/repositories/financeiro";

const AJUDA = {
  titulo: "Cadastro de Categorias Financeiras",
  descricao:
    "Categorias Financeiras detalham uma Categoria Mestra (ex.: dentro de 'Insumos Agrícolas', criar 'Fertilizantes' e 'Defensivos'). Elas são usadas para classificar cada lançamento em Contas a Pagar e Contas a Receber.",
  secoes: [
    {
      titulo: "Dados da Categoria",
      campos: [
        {
          nome: "Nome da Categoria",
          obrigatorio: true,
          explicacao: "Nome específico da categoria (ex.: 'Combustível', 'Manutenção de Máquinas').",
        },
        {
          nome: "Natureza da Operação",
          obrigatorio: true,
          explicacao: "Define se essa categoria representa uma Entrada (receita) ou Saída (despesa) — usado nos relatórios financeiros para separar o que entra do que sai.",
        },
        {
          nome: "Categoria Mestra",
          obrigatorio: true,
          explicacao: "O grupo principal ao qual esta categoria pertence. Se a categoria mestra desejada ainda não existe, use o botão '+' ao lado do campo para cadastrá-la sem sair desta tela.",
        },
      ],
    },
  ],
  dicas: [
    "Uma categoria com movimentações (contas a pagar/receber) vinculadas não pode ser excluída — o botão de excluir fica desabilitado nesse caso.",
  ],
};

export default function GestaoCategoriasFinanceiras() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [categorias, setCategorias] = useState([]);
  const [categoriasFiltradas, setCategoriasFiltradas] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  
  // Dados para o select de categorias mestras
  const [categoriasMestras, setCategoriasMestras] = useState([]);
  const [mostrarModalMestra, setMostrarModalMestra] = useState(false);

  const [filtros, setFiltros] = useState({
    busca: "",
    natureza: ""
  });
  
  const [formData, setFormData] = useState({
    nome: "",
    natureza: "entrada",
    mestra: "",
  });

  // Carregar categorias mestras
  const carregarCategoriasMestras = useCallback(async () => {
    try {
      const response = await repositorioCategoriaMestra.listar();
      const dados = response.data.results || response.data;
      setCategoriasMestras(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar categorias mestras:", error);
      setCategoriasMestras([]);
    }
  }, []);

  // Carregar categorias financeiras
  const carregarCategorias = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioCategoriaFinanceira.listar();
      const dados = response.data.results || response.data;
      setCategorias(Array.isArray(dados) ? dados : []);
      setCategoriasFiltradas(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar categorias financeiras:", error);
      alert("Erro ao carregar lista de categorias financeiras");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarCategorias();
    carregarCategoriasMestras();
  }, [carregarCategorias, carregarCategoriasMestras]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validarFormulario = () => {
    if (!formData.nome.trim()) {
      alert("Preencha o nome da categoria financeira.");
      return false;
    }
    if (!formData.natureza) {
      alert("Selecione a natureza da operação.");
      return false;
    }
    if (!formData.mestra) {
      alert("Selecione a categoria mestra.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    
    setLoading(true);

    const payload = {
      nome: formData.nome,
      natureza: formData.natureza,
      mestra: formData.mestra,
    };

    try {
      if (editando) {
        await repositorioCategoriaFinanceira.atualizar(editandoId, payload);
        alert("Categoria financeira atualizada com sucesso!");
        handleCancel();
        carregarCategorias();
      } else {
        await repositorioCategoriaFinanceira.criar(payload);
        alert("Categoria financeira cadastrada com sucesso!");
        handleCancel();
        carregarCategorias();
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (categoria) => {
    setFormData({
      nome: categoria.nome || "",
      natureza: categoria.natureza || "entrada",
      mestra: categoria.mestra?.id || categoria.mestra || "",
    });
    setEditando(true);
    setEditandoId(categoria.id);
    setMostrarFormulario(true);
    
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleNovaCategoria = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      nome: "",
      natureza: "entrada",
      mestra: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir esta categoria financeira? Esta ação não pode ser desfeita.")) {
      return;
    }
    
    try {
      await repositorioCategoriaFinanceira.excluir(id);
      alert("Categoria financeira excluída com sucesso!");
      carregarCategorias();
      
      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir categoria financeira. Verifique se não há registros vinculados.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = Array.isArray(categorias) ? [...categorias] : [];
    
    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(c => 
        c.nome?.toLowerCase().includes(buscaLower) ||
        c.mestra_nome?.toLowerCase().includes(buscaLower)
      );
    }
    
    if (filtros.natureza) {
      filtrados = filtrados.filter(c => c.natureza === filtros.natureza);
    }
    
    setCategoriasFiltradas(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "", natureza: "" });
    setCategoriasFiltradas(Array.isArray(categorias) ? categorias : []);
  };

  const getNaturezaLabel = (natureza) => {
    return natureza === "entrada" ? "Entrada" : "Saída";
  };

  const getNaturezaColor = (natureza) => {
    return natureza === "entrada" 
      ? "bg-green-100 text-green-800" 
      : "bg-red-100 text-red-800";
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">Gestão de Categorias Financeiras <SlGraph size={38} /></h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovaCategoria}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Categoria Financeira
            </button>
          )}
        </div>
        
        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Categoria Financeira" : "Cadastrar Nova Categoria Financeira"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Nome da Categoria *</label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      placeholder="Ex: Alimentação, Combustível, Medicamentos, etc."
                      required
                    />
                    <span className="text-xs text-gray-500 mt-1">
                      Nome descritivo da categoria financeira
                    </span>
                  </div>

                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Natureza da Operação *</label>
                    <select
                      name="natureza"
                      value={formData.natureza}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      required
                    >
                      <option value="entrada">Entrada (Receita)</option>
                      <option value="saida">Saída (Despesa)</option>
                    </select>
                    <span className="text-xs text-gray-500 mt-1">
                      Define se é uma receita ou despesa
                    </span>
                  </div>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Categoria Mestra *</label>
                  <div className="flex items-center gap-2">
                    <select
                      name="mestra"
                      value={formData.mestra}
                      onChange={handleChange}
                      className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      required
                    >
                      <option value="">Selecione uma categoria mestra...</option>
                      {Array.isArray(categoriasMestras) && categoriasMestras.length > 0 ? (
                        categoriasMestras.map(m => (
                          <option key={m.id} value={m.id}>
                            {m.nome}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>Nenhuma categoria mestra encontrada</option>
                      )}
                    </select>
                    <button
                        type="button"
                        onClick={() => setMostrarModalMestra(true)}
                        title="Cadastrar nova categoria mestra"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                  </div>
                  <span className="text-xs text-gray-500 mt-1">
                    Grupo principal ao qual esta categoria pertence
                  </span>
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}
        
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filtros de Busca
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Buscar por Nome ou Categoria Mestra</label>
                  <input
                    type="text"
                    placeholder="Digite o nome da categoria ou categoria mestra..."
                    value={filtros.busca}
                    onChange={(e) => setFiltros({...filtros, busca: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
                
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">Natureza</label>
                  <select
                    value={filtros.natureza}
                    onChange={(e) => setFiltros({...filtros, natureza: e.target.value})}
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  >
                    <option value="">Todas</option>
                    <option value="entrada">Entrada (Receita)</option>
                    <option value="saida">Saída (Despesa)</option>
                  </select>
                </div>
              </div>
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Categorias Financeiras */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-600">
                  {categoriasFiltradas.length} categoria(s) financeira(s) encontrada(s)
                </p>
              </div>
              
              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Categoria</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Natureza</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Categoria Mestra</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Qtd. Movimentações</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoriasFiltradas.map((categoria) => (
                        <tr key={categoria.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                          <td className="p-3 text-sm font-mono">{categoria.id}</td>
                          <td className="p-3 text-sm font-medium">{categoria.nome}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getNaturezaColor(categoria.natureza)}`}>
                              {getNaturezaLabel(categoria.natureza)}
                            </span>
                          </td>
                          <td className="p-3 text-sm">{categoria.mestra || "-"}</td>
                          <td className="p-3 text-sm">
                            <span className="font-medium">{categoria.movimentacoes_count || 0}</span>
                            <span className="text-gray-500 text-xs ml-1">movimentações</span>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditar(categoria)}
                                className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                title="Editar"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleExcluir(categoria.id)}
                                className={`px-3 py-1 rounded text-sm transition-colors ${
                                  categoria.movimentacoes_count > 0
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-red-600 hover:bg-red-700 text-white"
                                }`}
                                title={categoria.movimentacoes_count > 0 ? "Categoria com movimentações não pode ser excluída" : "Excluir"}
                                disabled={categoria.movimentacoes_count > 0}
                              >
                                Excluir
                              </button>
                            </div>
                           </td>
                         </tr>
                      ))}
                      
                      {categoriasFiltradas.length === 0 && (
                        <tr>
                          <td colSpan="6" className="p-8 text-center text-gray-500">
                            {filtros.busca || filtros.natureza
                              ? "Nenhuma categoria financeira encontrada com os filtros selecionados."
                              : "Nenhuma categoria financeira cadastrada."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <ModalCadastroCategoriaMestra
        isOpen={mostrarModalMestra}
        onClose={() => setMostrarModalMestra(false)}
        onSuccess={async (nova) => {
          await carregarCategoriasMestras();
          setFormData((prev) => ({ ...prev, mestra: nova.id }));
        }}
      />
    </Layout>
  );
}